<template>
  <div id="lg-magnit-page">
    <div class="sticky">
      <StickyMenu
        v-model="sticky"
        :current-menu="currentStickyMenu"
        @click="scrollToMenu"
      />
    </div>
    <div class="section section--fluid section--hero">
      <Hero :min-height="600" />
    </div>
    <div class="section section--intro">
      <Intro />
    </div>
    <div class="section section--3more">
      <ThreeMore
        :is-viewport-active="isViewportActive3More"
        @click:tab="handleClickTabMore"
      />
    </div>
    <div class="section section--3less">
      <ThreeLess
        :is-viewport-active="isViewportActive3Less"
        @click:tab="handleClickTabLess"
      />
    </div>
    <div class="section section--line-up">
      <LineUp />
    </div>
    <div class="section section--usage">
      <Usage @click="handleClickRecommended" />
    </div>
    <div class="section section--faq">
      <Faq />
    </div>

    <DialogRecommended
      v-model="dialogRecommendOpened"
      :recommended-items="currentRecommendedItems"
      @close="handleClickClose"
    />
  </div>
</template>

<script>
// import axios from 'axios'
import Hero from './components/Hero.vue'
import Intro from './components/Intro.vue'
import ThreeMore from './components/ThreeMore.vue'
import ThreeLess from './components/ThreeLess.vue'
import LineUp from './components/LineUp.vue'
import Usage from './components/Usage.vue'
import Faq from './components/Faq.vue'
import StickyMenu from './components/StickyMenu.vue'
import DialogRecommended from './components/DialogRecommended.vue'

export default {
  name: 'App',
  components: {
    Hero,
    Intro,
    ThreeMore,
    ThreeLess,
    LineUp,
    Usage,
    Faq,
    StickyMenu,
    DialogRecommended,
  },
  data() {
    return {
      // StickyMenu는 외부에서 v-model로만 사용 (내부에서 제어하지 않음)
      sticky: false,
      currentStickyMenu: -1,
      dialogRecommendOpened: false,
      currentRecommendedItems: [],

      menuTargets: [
        '.section--intro', // 메뉴 0
        '.section--line-up', // 메뉴 1
        '.section--usage', // 메뉴 2
        '.section--faq', // 메뉴 3
      ],

      // 스크롤/섹션 상태
      scrollY: 0,
      sectionsMeta: [], // [{ el, key, top, bottom, height }]
      activeSectionIndex: -1, // 현재 활성 섹션 인덱스
      activeSectionKey: '', // 표시용 key (예: 'section--intro')
      sectionProgress: 0, // 0 ~ 1 (상단선 기준 진행률)

      // 내부 유틸
      scrollTicking: false,
      recalcTimer: null,
      imgHandlers: null,
      videoHandlers: null,
      ro: null, // ResizeObserver (옵션)

      viewIO: null,
      viewTargets: [],

      // 관찰 결과 플래그 (computed에서 사용)
      is3MoreVisible: false,
      is3LessVisible: false,
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth <= 1080
    },
    sectionProgressFixed() {
      return this.sectionProgress.toFixed(3)
    },
    isViewportActive3More() {
      return this.is3MoreVisible
    },
    isViewportActive3Less() {
      return this.is3LessVisible
    },
  },
  mounted() {
    // 스크롤/리사이즈 리스너
    this.handleScroll()
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleResize, { passive: true })

    this.$nextTick(() => {
      // (기존 페이지의 GNB 관련 처리 유지)
      const headerElement = document.querySelector('.header')
      const contentElement = document.getElementById('content')
      if (headerElement && contentElement) {
        headerElement.style.position = 'relative'
        contentElement.style.paddingTop = 0
      }

      // 섹션 초기 수집 및 경계 계산
      this.initSections()
      this.updateSectionBounds()
      this.updateActiveSection()

      // 이미지/웹폰트/비디오 로딩에 따른 레이아웃 변화 대응
      this._observeImages()
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this._debouncedRecalc())
      }
      this._observeVideos()

      // (선택) 섹션 높이 변화 지속 반영
      this._setupResizeObserver()
      this.updateStickyMenu()

      this._setupViewportObserver()
    })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
    this._disconnectObservers()
    clearTimeout(this.recalcTimer)
    this._teardownViewportObserver()
  },
  methods: {
    handleClickTabMore(index) {
      // Three More 탭 클릭 처리
      console.log(`Three More tab clicked: ${index}`)
      this.$nextTick(() => {
        this._setupViewportObserver()
      })
    },
    handleClickTabLess(index) {
      // Three Less 탭 클릭 처리
      console.log(`Three Less tab clicked: ${index}`)
      this.$nextTick(() => {
        this._setupViewportObserver()
      })
    },
    // ----- Dialog -----
    handleClickRecommended(items) {
      this.currentRecommendedItems = items
      this.dialogRecommendOpened = true
    },
    handleClickClose() {
      this.dialogRecommendOpened = false
      this.currentRecommendedItems = []
    },

    // ----- Scroll / Resize -----
    handleScroll() {
      if (this.scrollTicking) return
      this.scrollTicking = true

      window.requestAnimationFrame(() => {
        this.scrollY = window.scrollY || window.pageYOffset || 0
        this.updateActiveSection()
        this.scrollTicking = false

        // 디버그 로그 (필요시 주석 해제)
        // console.log(
        //   `scrollY=${this.scrollY}, active=${this.activeSectionIndex} (${
        //     this.activeSectionKey
        //   }), progress=${this.sectionProgress.toFixed(3)}`
        // )
        this.updateStickyMenu()
      })
    },
    handleResize() {
      // 뷰포트 변경 → 경계 재계산
      this._debouncedRecalc()
      this.updateStickyMenu()
      this.windowWidth = window.innerWidth
    },
    updateStickyMenu() {
      // Sticky 메뉴 노출/숨김
      if (this.activeSectionIndex === 0 && this.sectionProgressFixed <= 0.8) {
        this.sticky = false
      } else {
        this.sticky = true
      }

      // Sticky 메뉴 3 More 3 Less 활성화 (0)
      if (
        (this.activeSectionIndex === 0 && this.sectionProgressFixed > 0.8) ||
        this.activeSectionIndex === 1 ||
        this.activeSectionIndex === 2 ||
        (this.activeSectionIndex === 3 && this.sectionProgressFixed <= 0.9)
      ) {
        this.currentStickyMenu = 0
      }
      // Sticky 메뉴 Line Up 활성화 (1)
      else if (
        (this.activeSectionIndex === 3 && this.sectionProgressFixed > 0.9) ||
        (this.activeSectionIndex === 4 && this.sectionProgressFixed <= 0.9)
      ) {
        this.currentStickyMenu = 1
      }
      // Sticky 메뉴 Usage Case 활성화 (2)
      else if (
        (this.activeSectionIndex === 4 && this.sectionProgressFixed > 0.9) ||
        (this.activeSectionIndex === 5 && this.sectionProgressFixed <= 0.9)
      ) {
        this.currentStickyMenu = 2
      }
      // Sticky 메뉴 FAQ 활성화 (3)
      else if (
        (this.activeSectionIndex === 5 && this.sectionProgressFixed > 0.9) ||
        this.activeSectionIndex === 6
      ) {
        this.currentStickyMenu = 3
      }
    },
    // ----- Section Init / Bounds / Active -----
    initSections() {
      const root = document.getElementById('lg-magnit-page')
      const nodes = root ? Array.from(root.querySelectorAll('.section')) : []
      this.sectionsMeta = nodes.map((el, idx) => {
        const cls = Array.from(el.classList).find((c) =>
          c.startsWith('section--')
        )
        const key = cls || `section-${idx}`
        return { el, key, top: 0, bottom: 0, height: 0 }
      })
    },
    updateSectionBounds() {
      const scrollY = window.scrollY || window.pageYOffset || 0
      this.sectionsMeta = this.sectionsMeta
        .map((s) => {
          // 정확한 문서 좌표: rect.top(뷰포트) + scrollY
          const top = s.el.getBoundingClientRect().top + scrollY
          const height = s.el.offsetHeight
          return { ...s, top, height, bottom: top + height }
        })
        .sort((a, b) => a.top - b.top)
    },
    updateActiveSection() {
      const scrollY = window.scrollY || window.pageYOffset || 0
      const EPS = 0.5 // 경계 튐 방지용 미세 여유

      // "섹션 윗변(top)이 브라우저 상단선(scrollY)을 통과한" 섹션 중 가장 마지막 것
      let foundIndex = 0
      for (let i = 0; i < this.sectionsMeta.length; i++) {
        if (this.sectionsMeta[i].top <= scrollY + EPS) {
          foundIndex = i
        } else {
          break
        }
      }

      const active = this.sectionsMeta[foundIndex]
      // 진행률: 상단선 기준 (0~1)
      const raw = (scrollY - active.top) / Math.max(1, active.height)
      this.sectionProgress = Math.max(0, Math.min(1, raw))
      this.activeSectionKey = active.key

      if (foundIndex !== this.activeSectionIndex) {
        this.activeSectionIndex = foundIndex
        // 필요 시: this.$emit('change:section', foundIndex)
      }
    },

    // ----- Recalc Utilities -----
    _debouncedRecalc() {
      clearTimeout(this.recalcTimer)
      this.recalcTimer = setTimeout(() => {
        this.updateSectionBounds()
        this.updateActiveSection()
        this.updateStickyMenu()
      }, 50)
    },

    // ----- Observe Images / Videos / Resize -----
    _observeImages() {
      const imgs = Array.from(document.images || [])
      this.imgHandlers = []
      imgs.forEach((img) => {
        if (img.complete) return
        const onDone = () => this._debouncedRecalc()
        img.addEventListener('load', onDone, { once: true })
        img.addEventListener('error', onDone, { once: true })
        this.imgHandlers.push({ img, onDone })
      })
    },
    _observeVideos() {
      const videos = Array.from(document.querySelectorAll('video') || [])
      this.videoHandlers = []
      videos.forEach((v) => {
        const onMeta = () => this._debouncedRecalc()
        const onLoadedData = () => this._debouncedRecalc()
        v.addEventListener('loadedmetadata', onMeta)
        v.addEventListener('loadeddata', onLoadedData)
        this.videoHandlers.push({ v, onMeta, onLoadedData })
      })
    },
    _setupResizeObserver() {
      if (!('ResizeObserver' in window)) return
      this.ro = new ResizeObserver(() => this._debouncedRecalc())
      this.sectionsMeta.forEach((s) => this.ro.observe(s.el))
    },
    _disconnectObservers() {
      // 이미지 핸들러 해제
      if (this.imgHandlers) {
        this.imgHandlers.forEach(({ img, onDone }) => {
          img.removeEventListener('load', onDone)
          img.removeEventListener('error', onDone)
        })
        this.imgHandlers = null
      }
      // 비디오 핸들러 해제
      if (this.videoHandlers) {
        this.videoHandlers.forEach(({ v, onMeta, onLoadedData }) => {
          v.removeEventListener('loadedmetadata', onMeta)
          v.removeEventListener('loadeddata', onLoadedData)
        })
        this.videoHandlers = null
      }
      // ResizeObserver 해제
      if (this.ro) {
        this.ro.disconnect()
        this.ro = null
      }
    },
    scrollToMenu(index) {
      const selector = this.menuTargets[index]
      if (!selector) return
      const el = document.querySelector(selector)
      if (!el) return
      this.scrollToElement(el, { considerSticky: true })
    },
    scrollToElement(el, { considerSticky = true } = {}) {
      const rectTop = el.getBoundingClientRect().top
      const scrollY = window.pageYOffset || window.scrollY || 0
      const stickyOffset = considerSticky ? this.getVisibleStickyHeight() : 0
      const targetTop = rectTop + scrollY - stickyOffset
      // 네이티브 스무스 스크롤(지원 브라우저)
      try {
        window.scrollTo({ top: targetTop, behavior: 'smooth' })
      } catch (e) {
        // 폴백
        this._animateScrollTo(targetTop, 400)
      }
    },
    getVisibleStickyHeight() {
      const stickyEl = document.querySelector('#lg-magnit-page .sticky')
      if (!stickyEl) return 0
      const style = window.getComputedStyle(stickyEl)
      const isFixed = style.position === 'fixed'
      const isVisible =
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        stickyEl.offsetHeight > 0
      return isFixed && isVisible ? stickyEl.getBoundingClientRect().height : 0
    },
    _animateScrollTo(target, duration = 400) {
      const start = window.pageYOffset || window.scrollY || 0
      const change = target - start
      const startTime = performance.now()
      const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t)

      const step = (now) => {
        const elapsed = now - startTime
        const t = Math.min(1, elapsed / duration)
        const eased = easeInOutQuad(t)
        window.scrollTo(0, start + change * eased)
        if (t < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    },
    // 비디오 위치를 알기 위한 뷰포트 관찰자
    _setupViewportObserver() {
      // 이미 있으면 초기화
      this._teardownViewportObserver()

      // 관찰 대상 2개(페이지에 하나씩이라고 하셨으므로 querySelector 사용)
      const moreEl = document.querySelector('.three-more-content__media')
      const lessEl = document.querySelector('.three-less-content__media-items')

      // 없을 수도 있으므로 방어
      const candidates = [moreEl, lessEl].filter(Boolean)
      if (candidates.length === 0) return

      // sticky 영향 없이 "뷰포트에 보이기 시작"을 정확히 잡기 위해 threshold 0 사용
      // (필요 시 rootMargin으로 미세 보정 가능)
      const stickyH = this.getVisibleStickyHeight?.() || 0
      const options = {
        root: null,
        rootMargin: `${-(stickyH + 8)}px 0px 0px 0px`,
        threshold: 0, // 교차 비율 > 0 되는 순간 true, 0 되는 순간 false
      }

      this.viewIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const target = entry.target
          const visible = entry.isIntersecting === true

          // 어떤 타깃인지 분기
          if (moreEl && target === moreEl) {
            // iOS 자동재생 등과 무관한 '보임' 플래그만 업데이트
            this.is3MoreVisible = visible
          } else if (lessEl && target === lessEl) {
            this.is3LessVisible = visible
          }
        })
      }, options)

      candidates.forEach((el) => this.viewIO.observe(el))
      this.viewTargets = candidates
    },

    _teardownViewportObserver() {
      try {
        if (this.viewIO) {
          if (this.viewTargets && this.viewTargets.length) {
            this.viewTargets.forEach((el) => this.viewIO.unobserve(el))
          }
          this.viewIO.disconnect()
        }
      } catch (_) {
        console.warn('Viewport observer teardown failed')
      }
      this.viewIO = null
      this.viewTargets = []
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/scss/function.scss';
#lg-magnit-page {
  background-color: #f0ece4;
  width: 100%;
  overflow: hidden;
  .sticky {
    width: 100%;
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    background-color: #f0ece4;
  }
  .section {
    position: relative;
    max-width: 1440px;
    margin: 0 auto;
    &--fluid {
      max-width: 100%;
    }
    &--intro {
      max-width: 1300px;
      padding: 80px 0 64px;
    }
    &--3more {
      max-width: 1280px;
      padding: 56px 0 60px;
    }
    &--3less {
      max-width: 1280px;
      padding: 60px 0;
    }
    &--line-up {
      max-width: 1280px;
      padding: 60px 0;
    }
    &--usage {
      padding: 60px 0;
    }
    &--faq {
      padding-top: 60px;
      padding-bottom: 120px;
    }
    @media screen and (max-width: 1080px) {
      &--intro {
        padding: calcQuarter(170) 0 calcQuarter(96);
      }
      &--3more {
        padding: calcQuarter(96) 0;
      }
      &--3less {
        padding: calcQuarter(96) 0;
      }
      &--line-up {
        padding: calcQuarter(96) 0;
      }
      &--usage {
        padding: calcQuarter(96) 0;
      }
      &--faq {
        padding: calcQuarter(96) 0 calcQuarter(196);
      }
    }
  }
}

@media screen and (max-width: 1080px) {
}
</style>
